export const PARENT_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

export type ParentLockReason = "manual" | "idle" | "hidden" | "pagehide" | "destroy";

export interface ParentSessionTimerApi {
  setTimeout: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
}

const DEFAULT_TIMERS: ParentSessionTimerApi = {
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: (timer) => clearTimeout(timer),
};

export class ParentSessionController {
  private unlocked = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  public constructor(
    private readonly onLock: (reason: ParentLockReason) => void,
    private readonly timers: ParentSessionTimerApi = DEFAULT_TIMERS,
  ) {}

  public isUnlocked(): boolean {
    return this.unlocked;
  }

  /** Prevent accidental persistence of the in-memory privilege state. */
  public toJSON(): Record<string, never> {
    return {};
  }

  public unlock(): void {
    if (this.destroyed) return;
    this.unlocked = true;
    this.scheduleIdleLock();
  }

  public recordActivity(): void {
    if (this.unlocked && !this.destroyed) this.scheduleIdleLock();
  }

  public lock(reason: ParentLockReason = "manual"): void {
    if (!this.unlocked) {
      this.clearTimer();
      return;
    }
    this.unlocked = false;
    this.clearTimer();
    this.onLock(reason);
  }

  public handleVisibility(hidden: boolean): void {
    if (hidden) this.lock("hidden");
  }

  public handlePageHide(): void {
    this.lock("pagehide");
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.lock("destroy");
    this.destroyed = true;
    this.clearTimer();
  }

  private scheduleIdleLock(): void {
    this.clearTimer();
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      this.lock("idle");
    }, PARENT_IDLE_TIMEOUT_MS);
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    this.timers.clearTimeout(this.timer);
    this.timer = null;
  }
}
